// Contratos escaneados: arrastar vários PDFs, acompanhar a leitura pela IA e abrir cada um para conferir e aplicar.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, FileUp, ScanText } from 'lucide-react';
import { api, enviarArquivo, ErroApi } from '../../api';
import { pode, useSessao } from '../../sessao';
import { NOME_STATUS_DOC, NOME_TIPO_CONTRATO, classeStatusDoc } from './comum';

interface Doc {
  id: string; nome_arquivo: string; bytes: number; status: string; erro: string | null; enviado_em: string; lido_em: string | null; aplicado_em: string | null;
  contrato_id: string | null; contrato: string | null; cliente_id: string | null; cliente: string | null; enviado_por: string;
  legibilidade: string | null; tipo_lido: string | null; pontos_atencao: number;
}
interface Lista { leitura_ligada: boolean; resumo: Record<string, number>; documentos: Doc[] }
interface Envio { nome: string; situacao: 'esperando' | 'enviando' | 'ok' | 'repetido' | 'erro'; mensagem?: string; id?: string }

const MAX_MB = 22;
const tamanho = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.ceil(b / 1024)} KB`);

export function Documentos({ contratoId, aoMudar }: { contratoId?: string; aoMudar?: () => void } = {}) {
  const { usuario } = useSessao();
  const podeEnviar = pode(usuario, 'contratos.editar');
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const situacao = contratoId ? 'todos' : params.get('situacao') ?? 'pendentes';
  const [lista, setLista] = useState<Lista | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [envios, setEnvios] = useState<Envio[]>([]);
  const [arrastando, setArrastando] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  const carregar = useCallback(() => {
    api<Lista>(`/contratos/documentos?situacao=${situacao}`).then(setLista).catch((e) => setErro(e.message));
  }, [situacao]);
  useEffect(() => { carregar(); }, [carregar]);
  // enquanto houver leitura em andamento, atualiza sozinho
  const emLeitura = (lista?.documentos ?? []).some((d) => d.status === 'na_fila' || d.status === 'lendo');
  useEffect(() => {
    if (!emLeitura) return;
    const t = setInterval(carregar, 8000);
    return () => clearInterval(t);
  }, [emLeitura, carregar]);

  async function enviar(arquivos: File[]) {
    const pdfs = arquivos.filter((a) => a.type === 'application/pdf' || a.name.toLowerCase().endsWith('.pdf'));
    const fila: Envio[] = arquivos.map((a) => (pdfs.includes(a)
      ? (a.size > MAX_MB * 1024 * 1024 ? { nome: a.name, situacao: 'erro', mensagem: `maior que ${MAX_MB} MB — escaneie em 200 dpi ou divida` } : { nome: a.name, situacao: 'esperando' })
      : { nome: a.name, situacao: 'erro', mensagem: 'não é PDF' }));
    setEnvios(fila);
    // um por vez: o servidor lê na ordem de chegada
    for (let i = 0; i < arquivos.length; i++) {
      if (fila[i]!.situacao !== 'esperando') continue;
      setEnvios((x) => x.map((e, j) => (j === i ? { ...e, situacao: 'enviando' } : e)));
      try {
        const qs = new URLSearchParams({ nome: arquivos[i]!.name.slice(0, 200) });
        if (contratoId) qs.set('contrato_id', contratoId);
        const r = await enviarArquivo<{ id: string; duplicado?: boolean }>(`/contratos/documentos?${qs}`, arquivos[i]!, 'application/pdf');
        setEnvios((x) => x.map((e, j) => (j === i ? { ...e, situacao: r.dados.duplicado ? 'repetido' : 'ok', id: r.dados.id } : e)));
      } catch (e) {
        setEnvios((x) => x.map((en, j) => (j === i ? { ...en, situacao: 'erro', mensagem: e instanceof ErroApi ? e.message : 'falha no envio' } : en)));
      }
    }
    carregar(); aoMudar?.();
  }

  const r = lista?.resumo ?? {};
  return (
    <>
      {!contratoId && <>
        <Link to="/contratos" className="voltar"><ArrowLeft size={16} aria-hidden /> Contratos</Link>
        <div className="cabecalho-pagina">
          <div><span className="kicker">Contratos</span><h1 className="titulo">Contratos escaneados</h1></div>
        </div>
        <p className="nota">Jogue aqui os PDFs dos contratos assinados. A IA lê cada um (preventivas, sistemas, SLA, peças, equipamentos em locação) e acha o cliente pelo CNPJ. <strong>Nada muda no contrato até alguém conferir e tocar em Aplicar.</strong></p>
      </>}
      {lista && !lista.leitura_ligada && <p className="alerta alerta-aviso">A leitura automática está desligada no servidor (sem chave da IA). Os arquivos ficam guardados na fila e são lidos quando ela for ligada.</p>}
      {erro && <p className="alerta" role="alert">{erro}</p>}

      {podeEnviar && (
        <div className={`area-soltar${arrastando ? ' arrastando' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setArrastando(true); }} onDragLeave={() => setArrastando(false)}
          onDrop={(e) => { e.preventDefault(); setArrastando(false); enviar([...e.dataTransfer.files]); }}>
          <FileUp size={26} strokeWidth={1.6} aria-hidden />
          <p><strong>Arraste os PDFs aqui</strong> ou <button type="button" className="link" onClick={() => entrada.current?.click()}>escolha no computador</button></p>
          <small>Vários de uma vez · até {MAX_MB} MB cada · até 100 páginas. O mesmo arquivo não entra duas vezes.</small>
          <input ref={entrada} type="file" accept="application/pdf,.pdf" multiple hidden onChange={(e) => { enviar([...(e.target.files ?? [])]); e.target.value = ''; }} />
        </div>
      )}
      {envios.length > 0 && (
        <ul className="lista-envios" aria-live="polite">
          {envios.map((e, i) => (
            <li key={i}><span>{e.nome}</span>
              <span className={`situacao ${e.situacao === 'ok' ? 's-ok' : e.situacao === 'erro' ? 's-nok' : e.situacao === 'repetido' ? 's-aviso' : 's-na'}`}>
                {e.situacao === 'esperando' ? 'na espera' : e.situacao === 'enviando' ? 'enviando…' : e.situacao === 'ok' ? 'na fila de leitura' : e.situacao === 'repetido' ? 'já enviado antes' : 'recusado'}
              </span>{e.mensagem && <small>{e.mensagem}</small>}</li>
          ))}
        </ul>
      )}

      {!contratoId && (
        <div className="barra-filtros">
          <div className="segmentos" role="group" aria-label="Filtro">
            {[['pendentes', `A conferir${r.lido ? ` (${r.lido})` : ''}`], ['aplicados', 'Aplicados'], ['todos', 'Todos']].map(([v, rot]) => (
              <button key={v} className={situacao === v ? 'ativo' : ''} onClick={() => setParams({ situacao: v! })}>{rot}</button>
            ))}
          </div>
          {emLeitura && <span className="nota"><ScanText size={15} aria-hidden /> lendo {(r.na_fila ?? 0) + (r.lendo ?? 0)} documento(s)… a lista atualiza sozinha</span>}
        </div>
      )}

      {lista && lista.documentos.length === 0 && !contratoId && <div className="cartao vazio"><p>Nenhum documento {situacao === 'pendentes' ? 'esperando conferência' : 'aqui'}.</p></div>}
      {lista && lista.documentos.length > 0 && (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela">
            <thead><tr><th>Arquivo</th><th>Cliente / contrato</th><th>Leitura</th><th>Situação</th></tr></thead>
            <tbody>
              {lista.documentos.filter((d) => !contratoId || d.contrato_id === contratoId).map((d) => (
                <tr key={d.id} onClick={() => navegar(`/contratos/documentos/${d.id}`)}>
                  <td><Link to={`/contratos/documentos/${d.id}`} onClick={(e) => e.stopPropagation()}><strong>{d.nome_arquivo}</strong></Link>
                    <small>{tamanho(d.bytes)} · {new Date(d.enviado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })} · {d.enviado_por}</small></td>
                  <td>{d.cliente ?? <span className="nota">cliente não identificado</span>}<small>{d.contrato ?? (d.status === 'lido' ? 'escolher o contrato' : '')}</small></td>
                  <td>{d.tipo_lido ? NOME_TIPO_CONTRATO[d.tipo_lido] ?? d.tipo_lido : '—'}
                    <small>{d.legibilidade ? `legibilidade ${d.legibilidade}` : ''}{d.pontos_atencao ? ` · ${d.pontos_atencao} ponto(s) de atenção` : ''}</small></td>
                  <td><span className={`situacao ${classeStatusDoc(d.status)}`}>{NOME_STATUS_DOC[d.status]}</span>{d.erro && <small title={d.erro}>{d.erro.slice(0, 80)}</small>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
